import * as React from 'react'
import { Box, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material'
import type { FormField } from '../../../types/Form/FormDefinition'
import { sortFormFieldOptions } from '../../../utils/dynamicFormsUtils'
import TagNumberField from './TagNumberField'

export type FieldValues = Record<string, string>
export type FieldErrors = Record<string, string | undefined>

type RequestFormSectionsProps = {
	sections: { sectionName: string; fields: FormField[] }[]
	values: FieldValues
	errors: FieldErrors
	projectId: number
	onFieldChange: (key: string, value: string) => void
	fieldFilter?: (f: FormField) => boolean
}

type RequestFieldProps = {
	field: FormField
	value: string
	error?: string
	projectId: number
	onFieldChange: (key: string, value: string) => void
}

function RequestField({ field: f, value, error, projectId, onFieldChange }: RequestFieldProps) {
	const common = {
		label: f.label,
		size: 'small' as const,
		fullWidth: true,
		required: f.isRequired,
		helperText: error ?? f.helpText ?? undefined,
		placeholder: f.placeholder ?? undefined,
		error: Boolean(error),
		sx: {
			'& .MuiOutlinedInput-root': { borderRadius: 1.5 },
		},
	}

	if (f.fieldKey === 'tagNumber') {
		return (
			<TagNumberField
				field={f}
				projectId={projectId}
				value={value}
				error={error}
				onChange={(next) => onFieldChange(f.fieldKey, next)}
			/>
		)
	}

	if (f.fieldType === 'dropdown') {
		const opts = sortFormFieldOptions(f)

		return (
			<TextField
				select
				{...common}
				value={value}
				onChange={(e) => onFieldChange(f.fieldKey, e.target.value)}
				InputLabelProps={{ shrink: true }}
				SelectProps={{ displayEmpty: !f.isRequired }}
			>
				{!f.isRequired && <MenuItem value=''>None</MenuItem>}
				{opts.length === 0 ? (
					<MenuItem value='' disabled>
						No options configured
					</MenuItem>
				) : (
					opts.map((o) => (
						<MenuItem key={o.id} value={o.value}>
							{o.value}
						</MenuItem>
					))
				)}
			</TextField>
		)
	}

	if (f.fieldType === 'number' || f.fieldType === 'currency') {
		return (
			<TextField
				{...common}
				type='number'
				value={value}
				onChange={(e) => onFieldChange(f.fieldKey, e.target.value)}
				inputProps={f.fieldType === 'currency' ? { step: '0.01' } : { step: 'any' }}
			/>
		)
	}

	if (f.fieldType === 'date') {
		return (
			<TextField
				{...common}
				type='date'
				value={value}
				onChange={(e) => onFieldChange(f.fieldKey, e.target.value)}
				InputLabelProps={{ shrink: true }}
			/>
		)
	}

	const multiline =
		Boolean(f.placeholder && f.placeholder.length > 40) ||
		f.fieldKey.toLowerCase().includes('notes')

	return (
		<TextField
			{...common}
			value={value}
			onChange={(e) => onFieldChange(f.fieldKey, e.target.value)}
			multiline={multiline}
			minRows={multiline ? 3 : undefined}
		/>
	)
}

export default function RequestFormSections({
	sections,
	values,
	errors,
	projectId,
	onFieldChange,
	fieldFilter = () => true,
}: RequestFormSectionsProps) {
	const visibleCount = sections.reduce((n, s) => n + s.fields.filter(fieldFilter).length, 0)
	return (
		<Box>
			{visibleCount === 0 ? (
				<Typography color='text.secondary'>This form has no visible fields.</Typography>
			) : (
				<Stack spacing={0}>
					{sections.map((s, sectionIndex) => (
						<Box
							key={s.sectionName}
							sx={{
								pt: sectionIndex > 0 ? 3.5 : 0,
								mt: sectionIndex > 0 ? 3.5 : 0,
								borderTop: sectionIndex > 0 ? '1px solid' : 'none',
								borderColor: 'divider',
							}}
						>
							<Typography
								variant='subtitle1'
								component='h2'
								sx={{
									fontWeight: 600,
									letterSpacing: 0.02,
									mb: 2.5,
									color: 'primary.main',
									fontSize: { xs: '1rem', sm: '1.0625rem' },
								}}
							>
								{s.sectionName}
							</Typography>
							<Grid container spacing={{ xs: 2.5, sm: 3 }}>
								{s.fields.filter(fieldFilter).map((ff) => (
									<Grid
										key={ff.id}
										item
										xs={12}
										sm={ff.fieldKey.toLowerCase().includes('notes') ? 12 : 6}
									>
										<RequestField
											field={ff}
											value={values[ff.fieldKey] ?? ''}
											error={errors[ff.fieldKey]}
											projectId={projectId}
											onFieldChange={onFieldChange}
										/>
									</Grid>
								))}
							</Grid>
						</Box>
					))}
				</Stack>
			)}
		</Box>
	)
}
